<template>
  <div class="flex min-h-full flex-1 flex-col justify-center px-6 pt-24 sm:px-6 lg:px-8">
    <div class="mx-auto w-full max-w-sm">
      <WordmarkLogo class="mx-auto" />
    </div>

    <div class="mx-auto mt-8 flex w-full max-w-lg flex-col gap-6">
      <div class="text-center">
        <h1 class="text-xl font-semibold">{{ t("sessionLimit.title") }}</h1>
        <p class="mt-2 text-sm text-muted-foreground">{{ t("sessionLimit.description") }}</p>
      </div>

      <!-- Slot availability: one pip per login slot, plus a live status line
           that flips to "N free. You can continue" as slots are disconnected. -->
      <div v-if="slots.limit > 0" class="flex flex-col items-center gap-1.5">
        <div
          v-if="showPips"
          role="img"
          :aria-label="t('sessionLimit.slotsUsedAria', { used: slots.used, limit: slots.limit })"
          class="flex items-center gap-1.5"
        >
          <span
            v-for="i in Number(slots.limit)"
            :key="i"
            class="h-2.5 w-2.5 rounded-full"
            :class="i <= slots.used ? 'bg-foreground/80' : 'border border-muted-foreground/40 bg-transparent'"
            aria-hidden="true"
          ></span>
        </div>
        <p
          role="status"
          class="text-sm"
          :class="freeSlots > 0 ? 'font-medium text-green-600 dark:text-green-400' : 'text-muted-foreground'"
        >
          {{ freeSlots > 0 ? t("sessionLimit.slotsFree", freeSlots) : t("sessionLimit.slotsFull", slots.limit) }}
        </p>
      </div>

      <div v-if="loadFailed" class="flex flex-col items-center gap-3 rounded-md border border-border p-4">
        <p class="text-sm text-danger" role="alert">{{ t("sessionLimit.loadError") }}</p>
        <button
          type="button"
          class="rounded-md bg-secondary px-3 py-1.5 text-xs font-semibold text-secondary-foreground hover:bg-secondary/80"
          @click="refresh"
        >
          {{ t("sessionLimit.retry") }}
        </button>
      </div>

      <ul v-if="slots.consumers.length" class="divide-y divide-border rounded-md border border-border">
        <li v-for="consumer in slots.consumers" :key="consumer.kind + consumer.id" class="p-3">
          <div class="flex items-start gap-3">
            <div class="min-w-0 flex-1">
              <!-- Row 1: what this is, the kind plus every state chip,
                   including the agentic lifecycle chip, so related tags sit
                   together. -->
              <div class="flex flex-wrap items-center gap-2 text-sm font-medium text-foreground">
                <span>{{ kindLabel(consumer.kind) }}</span>
                <span
                  v-if="consumer.kind === 'session' && consumer.authMethod"
                  class="rounded-full bg-secondary px-2 py-0.5 text-xs text-muted-foreground ring-1 ring-secondary"
                >
                  {{ authMethodLabel(consumer.authMethod) }}
                </span>
                <span
                  v-if="consumer.status"
                  class="rounded-full bg-secondary px-2 py-0.5 text-xs text-muted-foreground ring-1 ring-secondary"
                >
                  {{ agenticStatusLabel(consumer.status) }}
                </span>
                <span
                  v-if="consumer.current"
                  class="rounded-full bg-secondary px-2 py-0.5 text-xs text-muted-foreground ring-1 ring-secondary"
                >
                  {{ t("sessionLimit.current") }}
                </span>
              </div>
              <!-- Row 2: who/where, name, IP and the device/browser icons
                   together. A long IPv6 truncates on its own, with the full
                   value on hover. -->
              <div
                v-if="consumer.name || consumer.ip || consumer.userAgent"
                class="mt-0.5 flex min-w-0 flex-wrap items-center gap-x-1.5 text-xs text-muted-foreground"
              >
                <span v-if="consumer.name" class="truncate">{{ consumer.name }}</span>
                <span v-if="consumer.name && consumer.ip" aria-hidden="true">·</span>
                <Tooltip v-if="consumer.ip" :text="consumer.ip" focusable class="max-w-[11rem] truncate align-bottom">{{
                  consumer.ip
                }}</Tooltip>
                <UserAgentDisplay
                  v-if="consumer.userAgent"
                  :parsed="consumer.parsedUserAgent"
                  :raw="consumer.userAgent"
                  icons-only
                  class="shrink-0 text-muted-foreground/70"
                />
              </div>

              <!-- Row 3: timeline, plain text. An agentic access shows when
                   its lifecycle last moved; a login session or link shows its
                   start time; both end with the expiry. -->
              <div v-if="consumer.status" class="mt-0.5 text-xs text-muted-foreground">
                <span v-if="consumer.statusChangedAt">{{
                  t("sessionLimit.statusSince", {
                    status: agenticStatusLabel(consumer.status),
                    relative: relative(consumer.statusChangedAt),
                  })
                }}</span>
                <span v-if="consumer.statusChangedAt && consumer.expiresAt"> · </span>
                <span v-if="consumer.expiresAt">{{
                  t("sessionLimit.expires", { relative: relative(consumer.expiresAt) })
                }}</span>
              </div>
              <div v-else class="mt-0.5 text-xs text-muted-foreground">
                <span v-if="consumer.createdAt">{{
                  consumer.kind === "session"
                    ? t("sessionLimit.loggedIn", { relative: relative(consumer.createdAt) })
                    : t("sessionLimit.since", { relative: relative(consumer.createdAt) })
                }}</span>
                <span v-if="consumer.createdAt && consumer.expiresAt"> · </span>
                <span v-if="consumer.expiresAt">{{
                  t("sessionLimit.expires", { relative: relative(consumer.expiresAt) })
                }}</span>
              </div>
            </div>
            <button
              v-if="!consumer.current"
              type="button"
              class="rounded-md bg-secondary px-3 py-1.5 text-xs font-semibold text-secondary-foreground hover:bg-secondary/80 disabled:opacity-50"
              :disabled="busy"
              @click="disconnect(consumer)"
            >
              {{ t("sessionLimit.disconnect") }}
            </button>
          </div>

          <div class="mt-1 text-xs">
            <button
              v-if="consumer.grants?.length"
              type="button"
              class="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"
              :aria-expanded="isExpanded(consumer)"
              @click="toggleGrants(consumer)"
            >
              <span aria-hidden="true">{{ isExpanded(consumer) ? "▾" : "▸" }}</span>
              {{ t("sessionLimit.grantCount", consumer.grants.length) }}
              <span class="sr-only">{{
                isExpanded(consumer) ? t("sessionLimit.hideGrants") : t("sessionLimit.showGrants")
              }}</span>
            </button>
            <span v-else class="text-muted-foreground/70">{{ t("sessionLimit.grantCount", 0) }}</span>

            <ul v-if="consumer.grants?.length && isExpanded(consumer)" class="mt-1 space-y-0.5 pl-4">
              <li v-for="(grant, i) in consumer.grants" :key="i" class="text-muted-foreground">
                <span class="text-foreground">{{ grant.knoc }}</span>
                <span v-if="grant.ip"> · {{ grant.ip }}</span>
                <span v-if="grant.expiresAt">
                  · {{ t("sessionLimit.expires", { relative: relative(grant.expiresAt) }) }}</span
                >
              </li>
            </ul>
          </div>
        </li>
      </ul>

      <p v-if="errorMessage" class="text-center text-sm text-danger" role="alert">{{ errorMessage }}</p>

      <div class="flex flex-col gap-3">
        <ThemedButton
          type="button"
          class="!h-10 w-full font-semibold shadow-sm"
          :disabled="busy || !canContinue"
          @click="claim"
        >
          {{ t("sessionLimit.continue") }}
        </ThemedButton>
        <button type="button" class="link mx-auto text-sm" @click="logout">
          {{ t("sessionLimit.logout") }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { DateTime } from "luxon";

import WordmarkLogo from "../components/WordmarkLogo.vue";
import Tooltip from "../components/ui/Tooltip.vue";
import UserAgentDisplay from "../components/ui/UserAgentDisplay.vue";
import ThemedButton from "../user/components/ThemedButton.vue";
import AuthService, { type SessionSlotConsumer, type SessionSlots } from "../services/auth.service";
import { useAuthStore } from "../pinia/auth";
import { useThemeStore } from "../pinia/theme";
import { authMethodLabel as authMethodLabelFor } from "../utils/authMethodLabel";

const { t } = useI18n();
const router = useRouter();
const authStore = useAuthStore();
const themeStore = useThemeStore();
themeStore.fetchTheme();

const slots = ref<SessionSlots>({ limit: 0, used: 0, hasSlot: false, consumers: [] });
const busy = ref(false);
const errorMessage = ref("");
const loadFailed = ref(false);
const expandedGrants = ref(new Set<string>());

const canContinue = computed(() => slots.value.used < slots.value.limit);
const freeSlots = computed(() => Math.max(0, slots.value.limit - slots.value.used));
// One pip per slot only when the allowance is small enough to read at a
// glance; an unbounded limit (MaxUint64, unreachable here anyway) or a large
// one falls back to the status line alone.
const showPips = computed(() => slots.value.limit >= 2 && slots.value.limit <= 12);

const kindLabel = (kind: string) => {
  if (kind === "session") return t("sessionLimit.kinds.session");
  if (kind === "agent") return t("sessionLimit.kinds.agent");
  return t("sessionLimit.kinds.link");
};

const authMethodLabel = (method: string) => authMethodLabelFor(t, method);

// The agentic-access lifecycle in the user's words: created (link made) →
// viewed (agent redeemed the link) → approved (user let it in).
const agenticStatusLabel = (status: string) => {
  if (status === "pending_link") return t("sessionLimit.agenticStatus.created");
  if (status === "pending_approval") return t("sessionLimit.agenticStatus.viewed");
  if (status === "approved") return t("sessionLimit.agenticStatus.approved");
  return status;
};

// A 1s tick so "logged in … ago" / "expires in …" stay live while the user
// sits on this screen (mirrors AgenticAccessPanel). relative() reads now.value,
// so every consumer's relative time re-renders each tick.
const now = ref(Date.now());
const relative = (iso: string) => DateTime.fromISO(iso).toRelative({ base: DateTime.fromMillis(now.value) }) ?? "";

function consumerKey(consumer: SessionSlotConsumer): string {
  return consumer.kind + consumer.id;
}

function isExpanded(consumer: SessionSlotConsumer): boolean {
  return expandedGrants.value.has(consumerKey(consumer));
}

function toggleGrants(consumer: SessionSlotConsumer) {
  const key = consumerKey(consumer);
  if (expandedGrants.value.has(key)) {
    expandedGrants.value.delete(key);
  } else {
    expandedGrants.value.add(key);
  }
}

async function refresh() {
  loadFailed.value = false;
  try {
    slots.value = await AuthService.getSessionSlots();
  } catch {
    // Without the list the user can't free a slot, surface it with a retry
    // instead of an unhandled rejection and a silently empty screen.
    loadFailed.value = true;
    return;
  }
  slots.value.consumers ??= [];
  if (slots.value.hasSlot) {
    // The session already holds a slot (e.g. claimed in another tab), no
    // reason to keep the user here.
    await proceedHome();
  }
}

async function disconnect(consumer: SessionSlotConsumer) {
  busy.value = true;
  errorMessage.value = "";
  try {
    await AuthService.revokeSessionSlot(consumer.kind, consumer.id);
    await refresh();
  } catch {
    errorMessage.value = t("sessionLimit.disconnectError");
  } finally {
    busy.value = false;
  }
}

async function claim() {
  busy.value = true;
  errorMessage.value = "";
  try {
    await AuthService.claimSessionSlot();
    await proceedHome();
  } catch {
    errorMessage.value = t("sessionLimit.stillFull");
    await refresh();
  } finally {
    busy.value = false;
  }
}

async function proceedHome() {
  await authStore.fetchStatus({ force: true }).catch(() => {});
  router.push({ name: "Home" });
}

async function logout() {
  await authStore.logout().catch(() => {});
  router.push({ name: "Login" });
}

onMounted(refresh);

const tickInterval = setInterval(() => {
  now.value = Date.now();
}, 1000);
onUnmounted(() => clearInterval(tickInterval));
</script>
